/**
 * Files a person picked or dropped, with their paths inside what they chose. A chosen folder's
 * own name is not part of the paths, so uploading the same folder again under another name
 * gives the same paths.
 */

export interface PickedFile {
  /** `/`-separated, such as `letters/1520.txt`. */
  readonly path: string;
  readonly file: File;
}

export interface PickedFiles {
  /** The chosen folder's or single file's name; `null` for several loose files. */
  readonly name: string | null;
  readonly files: readonly PickedFile[];
}

/** Files from a file input; a folder input (`webkitdirectory`) gives `webkitRelativePath`. */
export function pickedFromInput(list: FileList | null): PickedFiles | null {
  const files = [...(list ?? [])];
  if (files.length === 0) return null;
  const relative = files.map((file) => file.webkitRelativePath || file.name);
  const folder = commonFolder(relative);
  const picked = files.map((file, index) => ({ path: folder === null ? relative[index] ?? file.name : (relative[index] ?? file.name).slice(folder.length + 1), file }));
  return { name: folder ?? (files.length === 1 ? files[0]?.name ?? null : null), files: sortedByPath(picked) };
}

/** Files and folders dropped on a drop zone, read through the File System entries API. */
export async function pickedFromDrop(transfer: DataTransfer): Promise<PickedFiles | null> {
  const entries = [...transfer.items].map((item) => item.webkitGetAsEntry()).filter((entry) => entry !== null);
  if (entries.length === 0) return pickedFromInput(transfer.files);
  const only = entries.length === 1 ? entries[0] : undefined;
  if (only !== undefined && isDirectory(only)) return { name: only.name, files: sortedByPath(await filesBelow(only, "")) };
  const files: PickedFile[] = [];
  for (const entry of entries) {
    files.push(...(isDirectory(entry) ? await filesBelow(entry, `${entry.name}/`) : isFile(entry) ? [{ path: entry.name, file: await fileOf(entry) }] : []));
  }
  return files.length === 0 ? null : { name: files.length === 1 ? files[0]?.path ?? null : null, files: sortedByPath(files) };
}

function commonFolder(paths: readonly string[]): string | null {
  const first = paths[0]?.split("/")[0];
  return first !== undefined && paths.every((path) => path.startsWith(`${first}/`)) ? first : null;
}

function sortedByPath(files: readonly PickedFile[]): PickedFile[] {
  return [...files].sort((left, right) => (left.path < right.path ? -1 : left.path > right.path ? 1 : 0));
}

async function filesBelow(directory: FileSystemDirectoryEntry, prefix: string): Promise<PickedFile[]> {
  const files: PickedFile[] = [];
  for (const entry of await allEntries(directory)) {
    if (isDirectory(entry)) files.push(...await filesBelow(entry, `${prefix}${entry.name}/`));
    else if (isFile(entry)) files.push({ path: `${prefix}${entry.name}`, file: await fileOf(entry) });
  }
  return files;
}

/** `readEntries` answers in batches until it returns none. */
async function allEntries(directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = directory.createReader();
  const entries: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => { reader.readEntries(resolve, reject); });
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
}

function fileOf(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => { entry.file(resolve, reject); });
}

function isDirectory(entry: FileSystemEntry): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

function isFile(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile;
}
